import React, { useState } from 'react';
import {
  Table, Button, Modal, Form, FormItem, Input, Select, 
  Card, useToast, showConfirm, Tag
} from '../ui';
import type { Column } from '../ui';
import {
  PlusIcon, PencilIcon, TrashIcon, TagIcon, MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { useCategories, useCreateCategory, useUpdateCategory, useDeleteCategory } from '../hooks/api/useCategories';

export interface Category {
  id: string;
  name: string;
  description?: string;
  color: string;
  created_at?: string;
  updated_at?: string;
}

interface CategoryFormData {
  name: string;
  description?: string;
  color: string;
}

const CategoryManagement: React.FC = () => {
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  
  const { success, error: showError } = useToast();

  // React Query hooks
  const { 
    data: categoriesData, 
    isLoading: loading, 
    error: categoriesError 
  } = useCategories(currentPage, pageSize, searchTerm);

  const createCategoryMutation = useCreateCategory();
  const updateCategoryMutation = useUpdateCategory();
  const deleteCategoryMutation = useDeleteCategory();

  // Extract data from hooks
  const categories = categoriesData?.categories || [];
  const totalCount = categoriesData?.totalCount || 0;

  // Handle mutation errors
  React.useEffect(() => {
    if (categoriesError) {
      showError('Error fetching categories: ' + categoriesError.message);
    }
  }, [categoriesError, showError]);

  // Handle search with debouncing
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1); // Reset to first page when searching
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handlePageChange = (page: number, newPageSize?: number) => {
    setCurrentPage(page);
    if (newPageSize && newPageSize !== pageSize) {
      setPageSize(newPageSize);
      setCurrentPage(1); // Reset to first page when changing page size
    }
  };

  const handleAdd = () => {
    setEditingCategory(null);
    setModalVisible(true);
  };

  const handleEdit = (category: Category) => {
    setEditingCategory(category);
    setModalVisible(true);
  };

  const handleDelete = async (id: string, categoryName: string) => {
    const confirmed = await showConfirm({
      title: `Delete category "${categoryName}"?`,
      content: 'This action cannot be undone.',
      onConfirm: () => {},
      type: 'danger'
    });
    
    if (!confirmed) return;

    try {
      await deleteCategoryMutation.mutateAsync(id);
      success('Category deleted successfully');
    } catch (error: any) {
      showError('Error deleting category: ' + error.message);
    }
  };

  const handleSubmit = async (values: CategoryFormData) => {
    try {
      if (editingCategory) {
        await updateCategoryMutation.mutateAsync({
          id: editingCategory.id,
          ...values
        });
        success('Category updated successfully');
      } else {
        await createCategoryMutation.mutateAsync(values);
        success('Category created successfully');
      }

      handleModalClose();
    } catch (error: any) {
      showError('Error saving category: ' + error.message);
    }
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setEditingCategory(null);
  };

  const colorOptions = [
    { value: 'red', label: 'Red' },
    { value: 'blue', label: 'Blue' },
    { value: 'green', label: 'Green' },
    { value: 'yellow', label: 'Yellow' },
    { value: 'purple', label: 'Purple' },
    { value: 'pink', label: 'Pink' },
    { value: 'orange', label: 'Orange' },
    { value: 'cyan', label: 'Cyan' },
    { value: 'gray', label: 'Gray' },
    { value: 'indigo', label: 'Indigo' },
  ];

  const columns: Column<Category>[] = [
    {
      key: 'name',
      title: 'Name',
      dataIndex: 'name',
      sorter: (a: Category, b: Category) => a.name.localeCompare(b.name),
      render: (name: string, record: Category) => (
        <div className="flex items-center">
          <Tag 
            color={record.color as any} 
            variant="soft" 
            size="sm" 
            className="mr-3 font-medium"
          >
            {name}
          </Tag>
        </div>
      ),
    },
    {
      key: 'description',
      title: 'Description',
      dataIndex: 'description',
      render: (description: string) => (
        <span className="text-gray-600">
          {description || 'No description'}
        </span>
      ),
    },
    {
      key: 'color',
      title: 'Color',
      dataIndex: 'color',
      render: (color: string) => (
        <div className="flex items-center">
          <div 
            className={`w-4 h-4 rounded-full mr-2 bg-${color}-500`}
          ></div>
          <span className="text-sm font-medium capitalize">{color}</span>
        </div>
      ),
    },
    {
      key: 'created_at',
      title: 'Created',
      dataIndex: 'created_at',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      key: 'actions',
      title: 'Actions',
      width: 120,
      render: (_, record: Category) => (
        <div className="flex space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleEdit(record)}
            className="text-blue-600 hover:text-blue-800"
          >
            <PencilIcon className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              showConfirm({
                title: 'Delete Category',
                content: `Are you sure you want to delete "${record.name}"? This action cannot be undone.`,
                onConfirm: () => handleDelete(record.id, record.name),
                confirmText: 'Delete',
                type: 'danger'
              });
            }}
            className="text-red-600 hover:text-red-800"
          >
            <TrashIcon className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <TagIcon className="h-6 w-6 mr-2" />
          Category Management
        </h1>
        <p className="text-gray-600 mt-1">
          Manage dress categories with colors and descriptions
        </p>
      </div>

      <div className="flex justify-between items-center">
        <Button
          variant="primary"
          size="lg"
          onClick={handleAdd}
          className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 font-semibold px-6 py-3"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add New Category
        </Button>
        
        <div className="ml-auto">
          <Input
            placeholder="Search categories by name or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            leftIcon={<MagnifyingGlassIcon className="h-4 w-4" />}
            className="w-80"
          />
        </div>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={categories}
          loading={loading}
          rowKey="id"
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalCount,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            onChange: handlePageChange,
            onShowSizeChange: handlePageChange
          }}
        />
      </Card>

      <Modal
        isOpen={modalVisible}
        onClose={handleModalClose}
        title={editingCategory ? 'Edit Category' : 'Add New Category'}
        size="md"
      >
        <Form
          onSubmit={handleSubmit}
          initialValues={editingCategory || {}}
        >
          <div className="space-y-4">
            <FormItem name="name" label="Category Name" required>
              <Input
                placeholder="Enter category name"
                autoFocus
                maxLength={100}
              />
            </FormItem>

            <FormItem name="color" label="Color" required>
              <Select
                options={colorOptions}
                placeholder="Select a color"
                required
              />
            </FormItem>

            <FormItem name="description" label="Description">
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows={3}
                placeholder="Enter category description..."
                maxLength={500}
              />
            </FormItem>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <Button variant="outline" onClick={handleModalClose}>
              Cancel
            </Button>
            <Button type="submit" variant="primary">
              {editingCategory ? 'Update Category' : 'Add Category'}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default CategoryManagement; 